import React, { useState, useRef, useMemo } from 'react';
import { qcAPI } from '../api';
import { Camera, Check, Star, AlertTriangle, Upload, Loader2 } from 'lucide-react';

// ─── Static configuration ─────────────────────────────────────
const CHECKLIST_GROUPS = [
  {
    key: 'measurements', title: 'Measurements',
    items: [
      { key: 'chest', label: 'Chest' },
      { key: 'waist', label: 'Waist' },
      { key: 'length', label: 'Length' },
      { key: 'sleeve', label: 'Sleeve' },
    ],
  },
  {
    key: 'design', title: 'Design',
    items: [
      { key: 'front', label: 'Front' },
      { key: 'back', label: 'Back' },
      { key: 'sleeve', label: 'Sleeve' },
      { key: 'customizations', label: 'Customizations' },
    ],
  },
  {
    key: 'finishing', title: 'Finishing',
    items: [
      { key: 'threads', label: 'No loose threads' },
      { key: 'stitching', label: 'No defects' },
      { key: 'fabric', label: 'Fabric checked' },
      { key: 'cleaned', label: 'Cleaned & pressed' },
    ],
  },
];

const PHOTO_SLOTS = [
  { key: 'front', label: 'Front' },
  { key: 'back', label: 'Back' },
  { key: 'side', label: 'Side' },
  { key: 'detail', label: 'Detail' },
];

const ALTERATION_OPTIONS = [
  { value: 'none', label: 'None', color: '#166534' },
  { value: 'minor', label: 'Minor', color: '#D4AF37' },
  { value: 'major', label: 'Major', color: '#991B1B' },
];

const emptyChecklist = () => CHECKLIST_GROUPS.reduce((acc, g) => {
  acc[g.key] = g.items.reduce((sub, it) => { sub[it.key] = false; return sub; }, {});
  return acc;
}, {});

// ─── Inline component ─────────────────────────────────────
const QCInlineForm = ({ order, onComplete }) => {
  const [checklist, setChecklist] = useState(emptyChecklist);
  const [photos, setPhotos] = useState({ front: null, back: null, side: null, detail: null });
  const [previews, setPreviews] = useState({ front: null, back: null, side: null, detail: null });
  const [confidence, setConfidence] = useState(0);
  const [alteration, setAlteration] = useState('');
  const [declaration, setDeclaration] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const fileInputs = useRef({});

  const toggleItem = (group, item) => {
    setChecklist(prev => ({
      ...prev,
      [group]: { ...prev[group], [item]: !prev[group][item] },
    }));
  };

  const toggleAllInGroup = (group) => {
    const groupItems = CHECKLIST_GROUPS.find(g => g.key === group).items;
    const allChecked = groupItems.every(it => checklist[group][it.key]);
    setChecklist(prev => ({
      ...prev,
      [group]: groupItems.reduce((acc, it) => { acc[it.key] = !allChecked; return acc; }, {}),
    }));
  };

  const handleFile = (slot, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError(`${slot}: please upload an image file`);
      return;
    }
    setPhotos(p => ({ ...p, [slot]: file }));
    const reader = new FileReader();
    reader.onload = ev => setPreviews(p => ({ ...p, [slot]: ev.target.result }));
    reader.readAsDataURL(file);
    setError('');
  };

  const allChecked = useMemo(() => CHECKLIST_GROUPS.every(g =>
    g.items.every(it => checklist[g.key][it.key])
  ), [checklist]);
  const allPhotos = PHOTO_SLOTS.every(s => photos[s.key]);
  const canSubmit = allChecked && allPhotos && confidence > 0 && alteration && declaration && !submitting;

  // Live progress meter — helps tailor see how close they are
  const progress = useMemo(() => {
    let done = 0, total = 12 + 4 + 3; // checks + photos + (confidence, alteration, declaration)
    CHECKLIST_GROUPS.forEach(g => g.items.forEach(it => { if (checklist[g.key][it.key]) done += 1; }));
    PHOTO_SLOTS.forEach(s => { if (photos[s.key]) done += 1; });
    if (confidence > 0) done += 1;
    if (alteration) done += 1;
    if (declaration) done += 1;
    return Math.round((done / total) * 100);
  }, [checklist, photos, confidence, alteration, declaration]);

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true); setError('');
    try {
      const fd = new FormData();
      fd.append('checklist', JSON.stringify(checklist));
      fd.append('confidence_score', String(confidence));
      fd.append('alteration_prediction', alteration);
      fd.append('declaration', 'true');
      PHOTO_SLOTS.forEach(s => fd.append(s.key, photos[s.key], `${s.key}.jpg`));
      await qcAPI.submit(order.order_id, fd);
      onComplete && onComplete();
    } catch (err) {
      setError(err.message || 'QC submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid={`qc-inline-${order.order_id}`} className="pt-2">
      {/* Progress bar */}
      <div className="mb-3">
        <div className="flex items-center justify-between mb-1">
          <p className="text-[9px] tracking-[0.12em] uppercase" style={{ color: '#595959' }}>QC Progress</p>
          <p className="text-[10px] font-medium" style={{ color: progress === 100 ? '#166534' : '#D4AF37' }}>
            {progress}%
          </p>
        </div>
        <div className="h-1 w-full overflow-hidden" style={{ backgroundColor: '#EAEAEA' }}>
          <div className="h-full transition-all duration-300"
            style={{ width: `${progress}%`, backgroundColor: progress === 100 ? '#166534' : '#D4AF37' }} />
        </div>
      </div>

      {/* Checklist Groups — compact grid */}
      {CHECKLIST_GROUPS.map(group => {
        const groupComplete = group.items.every(it => checklist[group.key][it.key]);
        return (
          <div key={group.key} data-testid={`qc-group-${group.key}`} className="mb-3">
            <button
              data-testid={`qc-check-all-${group.key}`}
              onClick={() => toggleAllInGroup(group.key)}
              className="w-full flex items-center justify-between mb-1.5"
            >
              <p className="text-[10px] tracking-[0.12em] uppercase font-medium" style={{ color: '#0F0F0F' }}>
                {group.title}
              </p>
              <span className="text-[9px] tracking-[0.08em] uppercase"
                style={{ color: groupComplete ? '#166534' : '#D4AF37' }}>
                {groupComplete ? '✓ All' : 'Tap all'}
              </span>
            </button>
            <div className="grid grid-cols-2 gap-1.5">
              {group.items.map(item => {
                const active = checklist[group.key][item.key];
                return (
                  <button
                    key={item.key}
                    data-testid={`qc-item-${group.key}-${item.key}`}
                    onClick={() => toggleItem(group.key, item.key)}
                    className="flex items-center gap-2 px-2.5 py-2 border text-left min-h-[40px]"
                    style={{
                      borderColor: active ? '#0F0F0F' : '#E5E5E5',
                      backgroundColor: active ? '#0F0F0F' : '#FFF',
                      color: active ? '#FFF' : '#0F0F0F',
                    }}
                  >
                    <span className="w-4 h-4 flex items-center justify-center flex-shrink-0 border"
                      style={{
                        borderColor: active ? '#FFF' : '#BFBFBF',
                        backgroundColor: active ? '#FFF' : 'transparent',
                      }}>
                      {active && <Check className="w-3 h-3" style={{ color: '#0F0F0F' }} strokeWidth={3} />}
                    </span>
                    <span className="text-[11px] leading-tight">{item.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}

      {/* Photos */}
      <div data-testid="qc-photos" className="mb-3">
        <p className="text-[10px] tracking-[0.12em] uppercase font-medium mb-1.5" style={{ color: '#0F0F0F' }}>
          Photos <span className="text-[9px] normal-case" style={{ color: '#595959' }}>(tap to capture)</span>
        </p>
        <div className="grid grid-cols-4 gap-1.5">
          {PHOTO_SLOTS.map(slot => (
            <div key={slot.key}>
              <input
                ref={el => (fileInputs.current[slot.key] = el)}
                data-testid={`qc-photo-input-${slot.key}`}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={e => handleFile(slot.key, e)}
                className="hidden"
              />
              <button
                data-testid={`qc-photo-btn-${slot.key}`}
                onClick={() => fileInputs.current[slot.key]?.click()}
                className="w-full aspect-square border flex flex-col items-center justify-center gap-0.5 overflow-hidden relative"
                style={{
                  borderColor: previews[slot.key] ? '#166534' : '#BFBFBF',
                  backgroundColor: '#FFF',
                  borderStyle: previews[slot.key] ? 'solid' : 'dashed',
                }}
              >
                {previews[slot.key] ? (
                  <>
                    <img src={previews[slot.key]} alt={slot.label} className="absolute inset-0 w-full h-full object-cover" />
                    <div className="absolute bottom-0 left-0 right-0 py-0.5 flex items-center justify-center"
                      style={{ backgroundColor: 'rgba(15,15,15,0.8)' }}>
                      <span className="text-[8px] text-white tracking-[0.08em] uppercase">{slot.label}</span>
                    </div>
                  </>
                ) : (
                  <>
                    <Camera className="w-4 h-4" style={{ color: '#595959' }} strokeWidth={1.5} />
                    <span className="text-[9px]" style={{ color: '#595959' }}>{slot.label}</span>
                  </>
                )}
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Confidence + Alteration — combined row for compactness */}
      <div className="grid grid-cols-5 gap-2 mb-3">
        <div data-testid="qc-confidence" className="col-span-3">
          <p className="text-[10px] tracking-[0.12em] uppercase font-medium mb-1" style={{ color: '#0F0F0F' }}>
            Confidence
          </p>
          <div className="flex gap-0.5">
            {[1, 2, 3, 4, 5].map(n => (
              <button
                key={n}
                data-testid={`qc-star-${n}`}
                onClick={() => setConfidence(n)}
                className="p-0.5 flex-1"
              >
                <Star
                  className="w-6 h-6 mx-auto"
                  strokeWidth={1.5}
                  style={{
                    color: confidence >= n ? '#D4AF37' : '#BFBFBF',
                    fill: confidence >= n ? '#D4AF37' : 'transparent',
                  }}
                />
              </button>
            ))}
          </div>
        </div>
        <div data-testid="qc-alteration" className="col-span-2">
          <p className="text-[10px] tracking-[0.12em] uppercase font-medium mb-1" style={{ color: '#0F0F0F' }}>
            Alteration
          </p>
          <div className="flex gap-1">
            {ALTERATION_OPTIONS.map(opt => {
              const active = alteration === opt.value;
              return (
                <button
                  key={opt.value}
                  data-testid={`qc-alt-${opt.value}`}
                  onClick={() => setAlteration(opt.value)}
                  className="flex-1 py-2 text-[10px] tracking-[0.05em] uppercase border font-medium"
                  style={{
                    borderColor: active ? opt.color : '#E5E5E5',
                    backgroundColor: active ? opt.color : '#FFF',
                    color: active ? '#FFF' : '#595959',
                  }}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Declaration */}
      <button
        data-testid="qc-declaration-toggle"
        onClick={() => setDeclaration(v => !v)}
        className="w-full flex items-start gap-2.5 p-2.5 border mb-2 text-left"
        style={{
          borderColor: declaration ? '#0F0F0F' : '#E5E5E5',
          backgroundColor: declaration ? 'rgba(15,15,15,0.03)' : '#FFF',
        }}
      >
        <span className="w-4 h-4 border flex items-center justify-center flex-shrink-0 mt-0.5"
          style={{
            borderColor: declaration ? '#0F0F0F' : '#BFBFBF',
            backgroundColor: declaration ? '#0F0F0F' : 'transparent',
          }}>
          {declaration && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
        </span>
        <p className="text-[11px] leading-relaxed" style={{ color: '#0F0F0F' }}>
          I confirm this garment matches the customer's measurements and design.
        </p>
      </button>

      {error && (
        <div data-testid="qc-error" className="flex items-start gap-2 p-2.5 mb-2"
          style={{ backgroundColor: 'rgba(153,27,27,0.06)' }}>
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#991B1B' }} strokeWidth={1.5} />
          <p className="text-[11px]" style={{ color: '#991B1B' }}>{error}</p>
        </div>
      )}

      {/* Submit */}
      <button
        data-testid={`qc-submit-${order.order_id}`}
        onClick={handleSubmit}
        disabled={!canSubmit}
        className="w-full py-3 text-[12px] tracking-[0.12em] uppercase font-medium text-white flex items-center justify-center gap-2 disabled:opacity-40"
        style={{ backgroundColor: '#0F0F0F' }}
      >
        {submitting ? (
          <><Loader2 className="w-4 h-4 animate-spin" strokeWidth={2} /> Submitting…</>
        ) : (
          <><Upload className="w-4 h-4" strokeWidth={2} /> Approve & Mark Ready</>
        )}
      </button>
    </div>
  );
};

export default QCInlineForm;
